import React, { useState, useEffect } from "react";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import CustomMultiSelect from "../../../../common/formfields/CustomMultiSelect";
import apiServices from "../../../../../services/apiService";

const AssignParkingModal = ({ isOpen, onClose, onConfirm, selectedSlots = [], unitId }) => {
    const [userList, setUserList] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    const [formData, setFormData] = useState({
        vehicleNumber: "",
        vehicleType: "",
        mobileNumber: "",
        startDate: "",
        endDate: "",
        assignedUser: "",
        assignedUserId: "",
    });

    const vehicleTypeOptions = [
        { label: "2-Wheeler", value: "2-Wheeler" },
        { label: "4-Wheeler", value: "4-Wheeler" },
        { label: "EV", value: "EV" }
    ];

    const fetchUnitUsers = async () => {
        if (!unitId) return;
        setIsLoadingUsers(true);
        try {
            const res = await apiServices({
                method: "GET",
                url: `/CommUserDetails/getUserDetails?PageNumber=1&PageSize=100&UnitId=${unitId}`
            });
            if (res?.success || res?.data?.success) {
                const resData = res?.data?.Commuserdetailsdata || res?.data?.data || [];
                setUserList(resData);
            }
        } catch (error) {
            console.error("Failed to fetch unit users", error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setFormData({
                vehicleNumber: "",
                vehicleType: "",
                mobileNumber: "",
                startDate: new Date().toISOString().split('T')[0],
                endDate: "",
                assignedUser: "",
                assignedUserId: "",
            });
            if (unitId) {
                fetchUnitUsers();
            }
        }
    }, [isOpen, unitId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleConfirm = () => {
        onConfirm({
            ...formData,
            vehicleType: formData.vehicleType?.value || formData.vehicleType,
            slots: selectedSlots
        });
        onClose();
    };

    const isValid = formData.vehicleNumber && formData.vehicleType && formData.mobileNumber && formData.startDate;

    const modalFooter = (
        <div className="flex justify-end gap-3">
            <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-bold text-gray-600 dark:text-textsecondary bg-gray-100 dark:bg-actionitembg rounded-lg hover:bg-gray-200 dark:hover:opacity-85 transition-all"
            >
                Cancel
            </button>
            <button
                onClick={handleConfirm}
                disabled={selectedSlots.length === 0 || !isValid}
                className="px-5 py-2 text-sm font-bold text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center"
                style={{ backgroundColor: "var(--color-primaryaccent)" }}
            >
                Confirm Assignment
            </button>
        </div>
    );

    return (
        <CommonModal
            isOpen={isOpen}
            onClose={onClose}
            title="Assign Parking Bay"
            width="md:w-[500px]"
            footerContent={modalFooter}
        >
            <div className="flex flex-col gap-5 pt-1">
                {/* Selected Inventory */}
                <div>
                    <label className="block text-[11px] font-bold text-gray-800 dark:text-textprimary mb-1.5">
                        Selected Bay
                    </label>
                    <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-dashboardbg border border-gray-100 dark:border-bordercolor rounded-lg">
                        {selectedSlots.map((slot) => (
                            <span 
                                key={slot.id} 
                                className="px-2 py-0.5 bg-blue-50 dark:bg-actionitembg text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900 rounded-[6px] text-[10px] font-bold tracking-wide"
                            >
                                {slot.slotNumber || slot.id}
                            </span>
                        ))}
                        {selectedSlots.length === 0 && (
                            <span className="text-xs text-gray-400">No bays selected</span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {/* User Name */}
                    <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                            User Name
                        </label>
                        <CustomMultiSelect
                            options={userList.map(u => ({ label: `${u.FirstName || ''} ${u.LastName || ''}`.trim(), value: u.Id }))}
                            value={formData.assignedUserId ? [{ label: formData.assignedUser, value: formData.assignedUserId }] : []}
                            onChange={(opts) => {
                                const opt = opts.length > 0 ? opts[opts.length - 1] : null;
                                if (opt) {
                                    const selectedUser = userList.find(u => String(u.Id) === String(opt.value));
                                    if (selectedUser) {
                                        setFormData(prev => ({
                                            ...prev,
                                            assignedUserId: selectedUser.Id,
                                            assignedUser: `${selectedUser.FirstName || ''} ${selectedUser.LastName || ''}`.trim(),
                                            mobileNumber: selectedUser.MobileNumber || selectedUser.mobileNumber || prev.mobileNumber,
                                            vehicleNumber: selectedUser.VehicleNo || selectedUser.vehicleNo || prev.vehicleNumber
                                        }));
                                    }
                                } else {
                                    setFormData(prev => ({ ...prev, assignedUserId: "", assignedUser: "" }));
                                }
                            }}
                            placeholder={isLoadingUsers ? "Loading users..." : "Search user..."}
                            isDisabled={isLoadingUsers}
                        />
                    </div>

                    {/* Mobile Number */}
                    <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                            Mobile Number *
                        </label>
                        <input
                            type="tel"
                            name="mobileNumber"
                            value={formData.mobileNumber}
                            onChange={handleInputChange}
                            placeholder="Enter 10-digit number"
                            className="w-full text-sm px-3 py-2 border border-bordercolor bg-transparent dark:text-textprimary rounded-lg focus:outline-none focus:ring-1 focus:ring-primaryaccent"
                        />
                    </div>

                    {/* Vehicle Type */}
                    <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                            Vehicle Type *
                        </label>
                        <CustomSelect
                            options={vehicleTypeOptions}
                            value={formData.vehicleType}
                            onChange={(val) => setFormData(prev => ({ ...prev, vehicleType: val }))}
                            placeholder="Select Type"
                        />
                    </div>

                    {/* Vehicle Number */}
                    <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                            Vehicle Number *
                        </label>
                        <input
                            type="text"
                            name="vehicleNumber"
                            value={formData.vehicleNumber}
                            onChange={handleInputChange}
                            placeholder="e.g. MH 01 AB 1234"
                            className="w-full text-sm px-3 py-2 border border-bordercolor bg-transparent dark:text-textprimary rounded-lg focus:outline-none focus:ring-1 focus:ring-primaryaccent"
                        />
                    </div>

                    {/* Start Date */}
                    <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                            Start Date *
                        </label>
                        <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className="w-full text-sm px-3 py-2 border border-bordercolor bg-transparent dark:text-textprimary rounded-lg focus:outline-none focus:ring-1 focus:ring-primaryaccent"
                        />
                    </div>

                    {/* End Date */}
                    <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                            End Date (Optional)
                        </label>
                        <input
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            className="w-full text-sm px-3 py-2 border border-bordercolor bg-transparent dark:text-textprimary rounded-lg focus:outline-none focus:ring-1 focus:ring-primaryaccent"
                        />
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default AssignParkingModal;
